import type { AssignmentShift } from '../../../../../js/api';
import { StatusCodes } from '../../../constants/workflows';
import { getShiftStatusBucket } from '../../../utils/globalStyles';
import type { NewShiftValues } from './ShiftsTab';

/**
 * ISO `start_on` dates the add-shift calendar should paint amber as
 * "already scheduled on this assignment". Two sources:
 *
 * 1. Persisted shifts whose status still buckets (PROPOSED / ACCEPTED /
 *    CONFIRMED / FINALIZED). Terminal-negative shifts (CANCELED, DECLINED,
 *    WITHDRAWN) are skipped — that day is genuinely free again.
 * 2. Additions already staged in this drawer session. When the modal is
 *    re-opened to edit one of those additions its own date is excluded, so
 *    the date being edited doesn't paint as "taken" by itself.
 *
 * The result is deduped and sorted so BaseCalendar gets a stable input.
 */
export function getScheduledShiftDates(
  shifts: AssignmentShift[],
  stagedAdditions: NewShiftValues[] = [],
  editingAdditionIndex: number | null = null
): string[] {
  const dates = new Set<string>();

  shifts.forEach((shift) => {
    if (!shift.start_on) return;
    const code = shift.current_status?.code as StatusCodes | undefined;
    if (!code || !getShiftStatusBucket(code)) return;
    dates.add(shift.start_on);
  });

  stagedAdditions.forEach((addition, index) => {
    if (index === editingAdditionIndex) return;
    if (addition.start_on) dates.add(addition.start_on);
  });

  return Array.from(dates).sort();
}
